import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AGGREGATE_NAMES, CHAT_COMMANDS, EVENT_CHAIN_STATUS } from 'cpool-constants';
import { deriveChatId } from 'cpool-utils';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { APICommunicator } from '~/api/APICommunicator';
import { invalidateInboxUnreadQueries } from '~/features/whatsapp/utils/invalidateInboxUnreadQueries';
import { toastChatCommandError } from '~/features/whatsapp/utils/toastChatCommandError';
import { useChainAwareInvalidation } from '~/hooks/useChainAwareInvalidation';
import useUserInfo from '~/hooks/useUserInfo';
import { queryKeys } from '~/utils/queryKeys';

type StartChatPayload = {
    phoneNumberId: string;
    divisionId: string;
    customerIdentity: string;
    loanRequestId: string;
};

export const useStartChatMutation = () => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const user = useUserInfo();
    const invalidateOnChainComplete = useChainAwareInvalidation();

    return useMutation({
        mutationFn: async (payload: StartChatPayload) => {
            const chatId = deriveChatId(payload.phoneNumberId, payload.customerIdentity);
            const result = await APICommunicator.sendCommand(
                {
                    aggregateId: chatId,
                    aggregateName: AGGREGATE_NAMES.CHAT,
                    commandName: CHAT_COMMANDS.startChat,
                    payload,
                },
                { allowNoop: true },
            );
            return { chatId, id: result?.id };
        },
        onError: (error) => {
            toastChatCommandError(error, t, 'WHATSAPP_CHAT.ERROR.START_CHAT_FAILED');
        },
        onSuccess: ({ id: correlationId }) => {
            queryClient.invalidateQueries({ queryKey: queryKeys.getChats() });
            if (user) {
                invalidateInboxUnreadQueries(queryClient, user.id);
            }

            if (!correlationId) return; // startChat silent no-op only

            invalidateOnChainComplete({
                correlationId,
                onComplete: (status) => {
                    if (status === EVENT_CHAIN_STATUS.COMPLETED) return;
                    toast.error(t('WHATSAPP_CHAT.ERROR.START_CHAT_CONFIRMATION_TIMEOUT'));
                },
                queryKeys: user
                    ? [queryKeys.getChats(), queryKeys.getInboxUnreadSummary(user.id)]
                    : [queryKeys.getChats()],
            });
        },
    });
};
